import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Spinner,
} from "@fluentui/react-components";
import { DatePicker } from "@fluentui/react-datepicker-compat";
import { DismissRegular, ReceiptAddRegular } from "@fluentui/react-icons";
import { format, isValid, parse } from "date-fns";
import { useTranslation } from "react-i18next";
import { useAppTheme } from "hooks/useAppTheme";
import { ClientParametersDto, OfferAzureSearch } from "library/types/types";
import { getInsurerShortName } from "library/utils/insurerDisplayName";
import {
  getGroupInvoiceErrorMessage,
  useCreateGroupInvoice,
} from "state/queries/useCreateGroupInvoice";
import {
  DIFFERENT_PAYER_KEY,
  EMPTY_PAYER_FORM,
  InvoicePayerOption,
  InvoicePayerSection,
  PayerFormData,
  buildPayerFromForm,
  completePayerEmail,
  getPayerDisplayName,
  getPayerValidationError,
  partyFromSearchRow,
} from "components/InvoicePayer";

// --- Types ---

/** The line invoice (MTPL, else CASCO) of one selected offer, read from the Azure Search row */
interface GroupInvoiceLine {
  offerId: string;
  product: "MTPL" | "CASCO";
  vehicle: string;
  insurer: string | null;
  invoiceNumber: string;
  /** yyyy-MM-dd, empty when the row has no due date yet (index not refreshed) */
  dueDate: string;
  premium: number;
  discount: number;
  toPay: number;
}

// --- Helpers ---

const toIsoDate = (value: string | null | undefined): string => {
  if (!value) return "";
  return value.split("T")[0] ?? "";
};

const formatDisplayDate = (isoDate: string): string => {
  if (!isoDate) return "-";
  const d = parse(isoDate, "yyyy-MM-dd", new Date());
  return isValid(d) ? format(d, "dd.MM.yyyy") : isoDate;
};

const formatAmount = (amount: number): string =>
  amount.toLocaleString("en", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

/** Invoice line of a list row: the MTPL invoice when present, else the CASCO one (same rule as the backend) */
export const buildGroupInvoiceLine = (
  item: OfferAzureSearch,
): GroupInvoiceLine | null => {
  const isMtpl = !!item.InvoiceMtplInvoiceNumber;
  const isCasco = !isMtpl && !!item.InvoiceCascoInvoiceNumber;
  if (!isMtpl && !isCasco) return null;
  const premium =
    (isMtpl ? item.InvoiceMtplPremium : item.InvoiceCascoPremium) ?? 0;
  const discount =
    (isMtpl ? item.InvoiceMtplDiscount : item.InvoiceCascoDiscount) ?? 0;
  return {
    offerId: item.OfferId,
    product: isMtpl ? "MTPL" : "CASCO",
    vehicle: item.VehicleLicensePlate || item.VehicleVin || "-",
    insurer:
      (isMtpl ? item.InvoiceMtplInsurer : item.InvoiceCascoInsurer) || null,
    invoiceNumber:
      (isMtpl ? item.InvoiceMtplInvoiceNumber : item.InvoiceCascoInvoiceNumber) ||
      "",
    dueDate: toIsoDate(
      isMtpl ? item.InvoiceMtplDueDate : item.InvoiceCascoDueDate,
    ),
    premium,
    discount,
    toPay: premium - discount,
  };
};

/** Identity of a party for de-duplicating the offered payers: its code, else its name */
const partyKey = (party: ClientParametersDto): string =>
  (party.Code || getPayerDisplayName(party)).trim().toLowerCase();

/**
 * Payers offered for the group invoice: the clients of the selected offers, the payers of their line invoices
 * (MTPL, else CASCO) and of a previous group invoice (regrouping), each party once, in the order of the rows.
 */
export const buildGroupInvoicePayerOptions = (
  items: OfferAzureSearch[],
): InvoicePayerOption[] => {
  const options: InvoicePayerOption[] = [];
  const seen = new Set<string>();
  const add = (
    party: ClientParametersDto | null,
    source: InvoicePayerOption["source"],
  ) => {
    if (!party) return;
    const key = partyKey(party);
    if (!key || seen.has(key)) return;
    seen.add(key);
    options.push({ key, source, payer: party });
  };
  for (const item of items) {
    add(partyFromSearchRow(item, "Client"), "client");
    add(
      partyFromSearchRow(
        item,
        item.InvoiceMtplInvoiceNumber ? "InvoiceMtplPayer" : "InvoiceCascoPayer",
      ),
      "payer",
    );
    add(partyFromSearchRow(item, "GroupInvoicePayer"), "payer");
  }
  return options;
};

// --- Component ---

type Props = {
  open: boolean;
  /** Selected list rows (all in status Invoice) */
  items: OfferAzureSearch[];
  /** created is true when the group invoice was created and the list should be reloaded */
  onClose: (created: boolean) => void;
};

export const CreateGroupInvoiceDialog = ({ open, items, onClose }: Props) => {
  const { t } = useTranslation();
  const theme = useAppTheme();
  const createGroupInvoice = useCreateGroupInvoice();

  // the group invoice number (QG{yyyyMMdd}-{invoiceId}) is assigned by the backend, so the dialog has no number field
  const [dueDate, setDueDate] = useState(""); // yyyy-MM-dd
  const [submitError, setSubmitError] = useState<string | null>(null);
  // the payer of the group invoice (mandatory): one of the offered clients / payers, or a different payer
  const [payerKey, setPayerKey] = useState<string>("");
  const [differentPayer, setDifferentPayer] =
    useState<PayerFormData>(EMPTY_PAYER_FORM);
  // e-mail entered in the dialog when the chosen client / payer has none (the payer e-mail is mandatory)
  const [missingEmail, setMissingEmail] = useState("");

  const isSubmitting = createGroupInvoice.isPending;

  // everything needed comes from the Azure Search rows, so no backend call per offer
  const lines = useMemo(
    () =>
      items
        .map(buildGroupInvoiceLine)
        .filter((l): l is GroupInvoiceLine => l !== null),
    [items],
  );
  const payerOptions = useMemo(
    () => buildGroupInvoicePayerOptions(items),
    [items],
  );

  const selectedPayer = payerOptions.find((o) => o.key === payerKey)?.payer;
  const payer: ClientParametersDto | null =
    payerKey === DIFFERENT_PAYER_KEY
      ? buildPayerFromForm(differentPayer)
      : selectedPayer
        ? completePayerEmail(selectedPayer, missingEmail)
        : null;

  // defaults when the dialog opens: the latest due date of the selected invoices (today only when none has one;
  // a date in the past is shown as is and reported by the validation until it is moved forward); the payer when
  // only one is offered (otherwise the user chooses)
  useEffect(() => {
    if (!open) return;
    setSubmitError(null);
    const maxDueDate = lines.reduce(
      (max, l) => (l.dueDate > max ? l.dueDate : max),
      "",
    );
    setDueDate(maxDueDate || format(new Date(), "yyyy-MM-dd"));
    setPayerKey(payerOptions.length === 1 ? payerOptions[0]!.key : "");
    setDifferentPayer(EMPTY_PAYER_FORM);
    setMissingEmail("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const totals = useMemo(
    () =>
      lines.reduce(
        (acc, l) => ({
          premium: acc.premium + l.premium,
          discount: acc.discount + l.discount,
          toPay: acc.toPay + l.toPay,
        }),
        { premium: 0, discount: 0, toPay: 0 },
      ),
    [lines],
  );

  const validationError = useMemo((): string | null => {
    if (!dueDate) {
      return t(
        "qtg.groupInvoice.validation.dueDateRequired",
        "Due date is required",
      );
    }
    if (dueDate < format(new Date(), "yyyy-MM-dd")) {
      return t(
        "qtg.groupInvoice.validation.dueDatePast",
        "Due date cannot be in the past",
      );
    }
    if (!payerKey) {
      return t(
        "qtg.invoicePayer.validation.required",
        "Select the payer the invoice is addressed to",
      );
    }
    return getPayerValidationError(payer, t);
  }, [dueDate, payerKey, payer, t]);

  const canSubmit = !isSubmitting && lines.length > 1 && !validationError;

  const handleClose = () => {
    if (isSubmitting) return;
    onClose(false);
  };

  const handleCreate = async () => {
    if (!canSubmit || !payer) return;
    setSubmitError(null);
    try {
      await createGroupInvoice.mutateAsync({
        OfferIds: lines.map((l) => l.offerId),
        DueDate: dueDate,
        Payer: payer,
      });
      onClose(true);
    } catch (err: any) {
      setSubmitError(
        getGroupInvoiceErrorMessage(
          err,
          t(
            "qtg.groupInvoice.createError",
            "Failed to create group invoice. Please try again.",
          ),
        ),
      );
    }
  };

  const thStyle: React.CSSProperties = {
    textAlign: "left",
    padding: "6px 8px",
    borderBottom: `2px solid ${theme.semanticColors.bodyDivider}`,
    fontWeight: 600,
    fontSize: 12,
    whiteSpace: "nowrap",
    color: theme.semanticColors.bodyText,
  };
  const tdStyle: React.CSSProperties = {
    padding: "6px 8px",
    borderBottom: `1px solid ${theme.semanticColors.bodyDivider}`,
    fontSize: 12,
    verticalAlign: "top",
    color: theme.semanticColors.bodyText,
  };
  const numStyle: React.CSSProperties = {
    ...tdStyle,
    textAlign: "right",
    whiteSpace: "nowrap",
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(e, data) => !data.open && handleClose()}
    >
      <DialogSurface style={{ maxWidth: "90vw", width: "1000px" }}>
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                aria-label="close"
                icon={<DismissRegular />}
                onClick={handleClose}
                disabled={isSubmitting}
              />
            }
          >
            {t("qtg.groupInvoice.dialog.title", "Group invoice")}
          </DialogTitle>
          <DialogContent>
            {/* Payer: the clients of the selected offers may differ, the group invoice is addressed to one payer */}
            <div data-help="offers-group-invoice-payer" style={{ marginBottom: 12 }}>
              <InvoicePayerSection
                options={payerOptions}
                selectedKey={payerKey}
                onSelectedKeyChange={(key) => {
                  setPayerKey(key);
                  setMissingEmail("");
                }}
                differentPayer={differentPayer}
                onDifferentPayerChange={setDifferentPayer}
                missingEmail={missingEmail}
                onMissingEmailChange={setMissingEmail}
                disabled={isSubmitting}
              />
            </div>

            {/* Invoice data */}
            <div
              style={{
                display: "flex",
                gap: 16,
                alignItems: "flex-end",
                flexWrap: "wrap",
                marginBottom: 12,
              }}
            >
              <Field
                data-help="offers-group-invoice-due-date"
                label={t("qtg.groupInvoice.field.dueDate", "Due date")}
                hint={t(
                  "qtg.groupInvoice.field.dueDateHint",
                  "The latest due date of the selected invoices is offered",
                )}
                required
                size="small"
              >
                <DatePicker
                  value={
                    dueDate ? parse(dueDate, "yyyy-MM-dd", new Date()) : null
                  }
                  onSelectDate={(date: Date | null | undefined) => {
                    setDueDate(
                      date && isValid(date) ? format(date, "yyyy-MM-dd") : "",
                    );
                  }}
                  formatDate={(date?: Date) =>
                    date && isValid(date) ? format(date, "dd.MM.yyyy") : ""
                  }
                  placeholder="DD.MM.YYYY"
                  style={{ width: "150px" }}
                  minDate={new Date()}
                  size="small"
                  disabled={isSubmitting}
                />
              </Field>
            </div>

            {/* Invoice lines */}
            <div style={{ overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: 12,
                }}
              >
                <thead>
                  <tr>
                    <th style={thStyle}>#</th>
                    <th style={thStyle}>
                      {t("qtg.groupInvoice.table.vehicle", "Vehicle")}
                    </th>
                    <th style={thStyle}>
                      {t("qtg.groupInvoice.table.product", "Product")}
                    </th>
                    <th style={thStyle}>
                      {t("qtg.groupInvoice.table.insurer", "Insurer")}
                    </th>
                    <th style={thStyle}>
                      {t("qtg.groupInvoice.table.invoiceNumber", "Invoice No.")}
                    </th>
                    <th style={thStyle}>
                      {t("qtg.groupInvoice.table.dueDate", "Due date")}
                    </th>
                    <th style={{ ...thStyle, textAlign: "right" }}>
                      {t("qtg.groupInvoice.table.premium", "Premium, €")}
                    </th>
                    <th style={{ ...thStyle, textAlign: "right" }}>
                      {t("qtg.groupInvoice.table.discount", "Discount, €")}
                    </th>
                    <th style={{ ...thStyle, textAlign: "right" }}>
                      {t("qtg.groupInvoice.table.toPay", "To pay, €")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line, index) => (
                    <tr key={line.offerId}>
                      <td style={tdStyle}>{index + 1}</td>
                      <td style={tdStyle}>{line.vehicle}</td>
                      <td style={tdStyle}>{line.product}</td>
                      <td style={tdStyle}>
                        {line.insurer ? getInsurerShortName(line.insurer) : "-"}
                      </td>
                      <td style={tdStyle}>{line.invoiceNumber}</td>
                      <td style={tdStyle}>{formatDisplayDate(line.dueDate)}</td>
                      <td style={numStyle}>{formatAmount(line.premium)}</td>
                      <td style={numStyle}>{formatAmount(line.discount)}</td>
                      <td style={numStyle}>{formatAmount(line.toPay)}</td>
                    </tr>
                  ))}
                  <tr>
                    <td
                      colSpan={6}
                      style={{ ...tdStyle, fontWeight: 600, textAlign: "right" }}
                    >
                      {t("qtg.groupInvoice.table.total", "Total")}
                    </td>
                    <td style={{ ...numStyle, fontWeight: 600 }}>
                      {formatAmount(totals.premium)}
                    </td>
                    <td style={{ ...numStyle, fontWeight: 600 }}>
                      {formatAmount(totals.discount)}
                    </td>
                    <td style={{ ...numStyle, fontWeight: 600 }}>
                      {formatAmount(totals.toPay)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {(validationError || submitError) && (
              <div
                style={{
                  padding: 12,
                  marginTop: 12,
                  background: theme.semanticColors.errorBackground,
                  color: theme.semanticColors.errorText,
                  borderRadius: 4,
                }}
              >
                {submitError || validationError}
              </div>
            )}
          </DialogContent>

          <DialogActions>
            <Button
              appearance="secondary"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              {t("qtg.common.cancel", "Cancel")}
            </Button>
            <Button
              data-help="offers-group-invoice-create"
              appearance="primary"
              icon={isSubmitting ? <Spinner size="tiny" /> : <ReceiptAddRegular />}
              disabled={!canSubmit}
              onClick={handleCreate}
            >
              {isSubmitting
                ? t("qtg.common.processing", "Processing...")
                : t("qtg.groupInvoice.dialog.create", "Create group invoice")}
              {!isSubmitting && lines.length > 0 && ` (${lines.length})`}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
